<template>
  <div>
    <v-card>
      <v-tabs v-model="activeTab" class="cash-tabs" color="primary" slider-color="white" @change="onTabChanged">
        <v-tab>Hesaplar</v-tab>
        <v-tab>Snapshot Geçmişi</v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-card-text>
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <div class="cash-tab-section">
              <div class="refined-header">
                <div>
                  <h2 class="refined-title">Para &amp; Banka Hesapları</h2>
                  <p class="refined-subtitle">Hesapları oluşturun, düzenleyin ve bakiyeleri yönetin</p>
                </div>
                <button type="button" class="refined-primary-btn" @click="openAccountCreateDialog">
                  <v-icon small class="mr-1">add</v-icon>
                  Yeni Hesap
                </button>
              </div>

              <div class="refined-filters">
                <div class="refined-filter-item">
                  <label class="refined-filter-label">Şirket</label>
                  <select v-model="selectedCompanyId" class="refined-select" @change="loadAccounts">
                    <option :value="null">Tümü</option>
                    <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="refined-filter-item">
                  <label class="refined-filter-label">Durum</label>
                  <select v-model="statusFilter" class="refined-select" @change="loadAccounts">
                    <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                  </select>
                </div>
              </div>

              <div class="refined-table-wrap">
                <div class="refined-table-scroll">
                  <table class="refined-table">
                    <thead>
                      <tr>
                        <th>Ad</th>
                        <th>Şirket</th>
                        <th>Tip</th>
                        <th>IBAN</th>
                        <th>Durum</th>
                        <th>Bakiyeler</th>
                        <th class="cash-th-actions">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loading">
                        <td colspan="7" class="refined-loading">
                          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                          Yükleniyor...
                        </td>
                      </tr>
                      <tr v-else-if="!accounts.length">
                        <td colspan="7" class="refined-empty">Kayıt yok</td>
                      </tr>
                      <tr v-for="(item, i) in accounts" :key="item.id || i" class="refined-row">
                        <td class="refined-cell-bold">{{ item.name }}</td>
                        <td>
                          <span v-if="item.companyName" class="refined-chip cash-chip-company">{{ item.companyName }}</span>
                          <span v-else class="refined-muted">-</span>
                        </td>
                        <td>
                          <span class="refined-chip" :class="Number(item.accountType) === 1 ? 'cash-chip-vault' : 'cash-chip-bank'">
                            {{ Number(item.accountType) === 1 ? 'Kasa' : 'Banka' }}
                          </span>
                        </td>
                        <td>
                          <span v-if="item.iban" class="refined-cell-value">{{ item.iban }}</span>
                          <span v-else class="refined-muted">-</span>
                        </td>
                        <td>
                          <span class="refined-chip" :class="item.status === 'ACTIVE' ? 'refined-chip-success' : 'refined-chip-error'">
                            {{ item.status === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
                          </span>
                        </td>
                        <td>
                          <div v-if="(item.balances || []).length" class="cash-balances">
                            <span
                              v-for="(b, bi) in item.balances"
                              :key="bi"
                              class="cash-balance-chip"
                              :class="{ 'cash-balance-neg': Number(b.balance) < 0 }"
                            >
                              <span class="cash-balance-code">{{ b.symbol || b.code }}</span>
                              <span class="cash-balance-amount">{{ formatMoney(b.balance) }}</span>
                            </span>
                          </div>
                          <span v-else class="refined-muted">-</span>
                        </td>
                        <td class="cash-cell-actions">
                          <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                              <button type="button" class="refined-action-btn" v-on="on">İşlem</button>
                            </template>
                            <v-card class="refined-menu-card">
                              <div class="refined-menu-body">
                                <button type="button" class="refined-menu-btn refined-menu-btn-primary" @click="openAccountEditDialog(item)">DÜZENLE</button>
                                <div class="refined-menu-divider"></div>
                                <button type="button" class="refined-menu-btn refined-menu-btn-error" @click="confirmDelete(item)">SİL</button>
                              </div>
                            </v-card>
                          </v-menu>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="cash-tab-section">
              <div class="refined-header">
                <div>
                  <h2 class="refined-title">Snapshot Geçmişi</h2>
                  <p class="refined-subtitle">Günlük para/banka snapshot kayıtları</p>
                </div>
                <div class="refined-header-actions">
                  <v-menu
                    v-model="snapshotDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <button type="button" class="refined-date-btn" v-on="on">
                        <v-icon small class="mr-1">event</v-icon>
                        {{ snapshotDateFilter ? formatDateOnly(snapshotDateFilter) : 'Tümü' }}
                      </button>
                    </template>
                    <v-date-picker v-model="snapshotDateFilter" locale="tr-TR" @input="snapshotDateMenu = false; loadSnapshots()"></v-date-picker>
                  </v-menu>
                  <button type="button" class="refined-primary-btn" @click="snapshotDateFilter = ''; loadSnapshots()">
                    Tümü
                  </button>
                  <button type="button" class="refined-primary-btn refined-btn-success" :disabled="snapshotsLoading" @click="loadSnapshots">
                    <v-icon small class="mr-1">refresh</v-icon>
                    YENİLE
                  </button>
                </div>
              </div>

              <div class="refined-filters cash-excel-filters">
                <div class="refined-filter-item">
                  <label class="refined-filter-label">Başlangıç</label>
                  <v-menu
                    v-model="excelStartMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <button type="button" class="refined-date-btn" v-on="on">
                        <v-icon small class="mr-1">event</v-icon>
                        {{ excelStartDate ? formatDateOnly(excelStartDate) : 'Seçiniz' }}
                      </button>
                    </template>
                    <v-date-picker v-model="excelStartDate" locale="tr-TR" @input="excelStartMenu = false"></v-date-picker>
                  </v-menu>
                </div>
                <div class="refined-filter-item">
                  <label class="refined-filter-label">Bitiş</label>
                  <v-menu
                    v-model="excelEndMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <button type="button" class="refined-date-btn" v-on="on">
                        <v-icon small class="mr-1">event</v-icon>
                        {{ excelEndDate ? formatDateOnly(excelEndDate) : 'Seçiniz' }}
                      </button>
                    </template>
                    <v-date-picker v-model="excelEndDate" locale="tr-TR" @input="excelEndMenu = false"></v-date-picker>
                  </v-menu>
                </div>
                <div class="refined-filter-item cash-excel-btn-item">
                  <button type="button" class="refined-primary-btn refined-btn-success" :disabled="snapshotsLoading" @click="exportSnapshotsExcel">
                    <v-icon small class="mr-1">download</v-icon>
                    Excel İndir (Sekmeli)
                  </button>
                </div>
              </div>

              <div class="refined-table-wrap">
                <div class="refined-table-scroll">
                  <table class="refined-table">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Toplamlar</th>
                        <th>Hesap Sayısı</th>
                        <th class="cash-th-actions">Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="snapshotsLoading">
                        <td colspan="4" class="refined-loading">
                          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                          Yükleniyor...
                        </td>
                      </tr>
                      <tr v-else-if="!paginatedSnapshots.length">
                        <td colspan="4" class="refined-empty">Kayıt yok</td>
                      </tr>
                      <template v-for="(item, i) in paginatedSnapshots">
                        <tr :key="'row-' + i" class="refined-row">
                          <td class="refined-cell-bold">{{ formatDate(item.createdAt) }}</td>
                          <td>
                            <div v-if="getSnapshotTotals(item).length" class="cash-balances">
                              <span v-for="(t, ti) in getSnapshotTotals(item)" :key="ti" class="cash-balance-chip">
                                <span class="cash-balance-code">{{ snapVal(t, 'Symbol') || snapVal(t, 'Code') }}</span>
                                <span class="cash-balance-amount">{{ formatMoney(snapVal(t, 'Total')) }}</span>
                              </span>
                            </div>
                            <span v-else class="refined-muted">-</span>
                          </td>
                          <td>{{ getSnapshotAccounts(item).length }}</td>
                          <td class="cash-cell-actions">
                            <button type="button" class="refined-action-btn" @click="toggleSnapshotDetail(i)">
                              {{ expandedSnapshot === i ? 'Gizle' : 'Detay' }}
                            </button>
                          </td>
                        </tr>
                        <tr v-if="expandedSnapshot === i" :key="'detail-' + i" class="cash-detail-row">
                          <td colspan="4">
                            <div class="cash-detail-wrap">
                              <div v-if="!getSnapshotAccounts(item).length" class="refined-muted">Detay bulunamadı.</div>
                              <div v-for="(acc, ai) in getSnapshotAccounts(item)" :key="ai" class="cash-detail-card">
                                <div class="cash-detail-head">
                                  <span class="cash-detail-name">{{ snapVal(acc, 'Name') }}</span>
                                  <span class="refined-chip" :class="Number(snapVal(acc, 'AccountType')) === 1 ? 'cash-chip-vault' : 'cash-chip-bank'">
                                    {{ Number(snapVal(acc, 'AccountType')) === 1 ? 'Kasa' : 'Banka' }}
                                  </span>
                                  <span class="refined-chip" :class="snapVal(acc, 'Status') === 'ACTIVE' ? 'refined-chip-success' : 'refined-chip-error'">
                                    {{ snapVal(acc, 'Status') === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
                                  </span>
                                </div>
                                <div class="cash-balances">
                                  <span
                                    v-for="(b, bi) in getSnapshotAccountBalances(acc)"
                                    :key="bi"
                                    class="cash-balance-chip"
                                    :class="{ 'cash-balance-neg': Number(snapVal(b, 'Balance')) < 0 }"
                                  >
                                    <span class="cash-balance-code">{{ snapVal(b, 'Symbol') || snapVal(b, 'Code') }}</span>
                                    <span class="cash-balance-amount">{{ formatMoney(snapVal(b, 'Balance')) }}</span>
                                  </span>
                                  <span v-if="!getSnapshotAccountBalances(acc).length" class="refined-muted">-</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
                <div class="refined-table-footer">
                  <div class="refined-footer-left">
                    <span>Sayfa başına satır:</span>
                    <select v-model.number="snapshotsRowsPerPage" class="refined-rows-select">
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                    </select>
                  </div>
                  <span class="refined-pagination-info">{{ snapshotsPaginationInfo }}</span>
                  <div class="refined-footer-actions">
                    <button type="button" class="refined-page-btn" :disabled="snapshotsPage <= 1" @click="snapshotsPage = Math.max(1, snapshotsPage - 1)">
                      <v-icon small>chevron_left</v-icon>
                    </button>
                    <button type="button" class="refined-page-btn" :disabled="snapshotsPage >= snapshotsTotalPages" @click="snapshotsPage = Math.min(snapshotsTotalPages, snapshotsPage + 1)">
                      <v-icon small>chevron_right</v-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>

    <v-dialog v-model="accountDialog" max-width="720px" persistent>
      <v-card class="cash-modal">
        <v-card-title class="headline white--text elevation-2" :class="accountDialogHeadColor">
          <v-icon left color="white" size="28">{{ Number(accountModel.accountType) === 1 ? 'savings' : 'account_balance' }}</v-icon>
          <span class="font-weight-bold">{{ accountEditId ? 'Hesap Düzenle' : 'Yeni Hesap' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="accountDialog = false" :disabled="accountSaving">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 class="mb-1">
                <v-select
                  v-model="accountModel.companyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Şirket"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6 class="pr-sm-2">
                <v-text-field
                  v-model="accountModel.name"
                  label="Hesap Adı"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-sm-2">
                <v-select
                  v-model="accountModel.accountType"
                  :items="accountTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="Tip"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-select>
              </v-flex>
              <v-flex xs12 v-if="Number(accountModel.accountType) === 1">
                <v-alert :value="true" color="amber darken-2" icon="info" outline class="mb-2">
                  Kasa tipi hesaplar şirketten bağımsızdır; hangi şirket seçilirse seçilsin tüm şirketlerin hesap listelerinde görünür.
                </v-alert>
              </v-flex>
              <v-flex xs12 sm6 class="pr-sm-2">
                <v-text-field
                  v-model="accountModel.iban"
                  label="IBAN"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm3 class="px-sm-2">
                <v-select
                  v-model="accountModel.status"
                  :items="accountStatusOptions"
                  item-text="text"
                  item-value="value"
                  label="Durum"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm3 class="pl-sm-2">
                <v-text-field
                  v-model.number="accountModel.sortOrder"
                  label="Sıra"
                  type="number"
                  outlined
                  dense
                  :disabled="accountSaving"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <div v-if="accountEditId" class="cash-balance-section">
              <div class="cash-balance-section-title">Bakiyeler</div>
              <div v-if="!balanceRows.length" class="cash-balance-note">
                Aktif para birimi yok — Admin &gt; Para Birimleri bölümünden ekleyin.
              </div>
              <v-layout v-else wrap>
                <v-flex
                  v-for="(row, ri) in balanceRows"
                  :key="row.currencyId"
                  xs12
                  sm6
                  :class="ri % 2 === 0 ? 'pr-sm-2' : 'pl-sm-2'"
                >
                  <v-text-field
                    v-model.number="row.newBalance"
                    :label="row.symbol ? (row.code + ' (' + row.symbol + ')') : row.code"
                    type="number"
                    step="0.01"
                    outlined
                    dense
                    :disabled="accountSaving"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </div>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="accountDialog = false" class="px-4" :disabled="accountSaving">
            <v-icon left>close</v-icon>
            İptal
          </v-btn>
          <v-btn depressed class="white--text px-4" :class="accountDialogBtnColor" :loading="accountSaving" @click="saveAccount">
            <v-icon left>check</v-icon>
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Silme Onayı</v-card-title>
        <v-card-text>
          <strong>{{ accountToDelete && accountToDelete.name }}</strong> hesabını silmek istediğinizden emin misiniz?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" flat @click="deleteDialog = false" :disabled="deleting">İptal</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteAccount">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import companiesApi from '@/api/companies';

export default {
  data() {
    return {
      activeTab: 0,
      loading: false,
      accounts: [],
      companies: [],
      selectedCompanyId: null,
      statusFilter: '',
      statusOptions: [
        { text: 'Tümü', value: '' },
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Pasif', value: 'PASSIVE' },
      ],
      accountTypeOptions: [
        { text: 'Banka', value: 0 },
        { text: 'Kasa', value: 1 },
      ],
      accountStatusOptions: [
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Pasif', value: 'PASSIVE' },
      ],
      accountDialog: false,
      accountSaving: false,
      accountEditId: null,
      accountModel: {
        companyId: null,
        name: '',
        accountType: 0,
        iban: '',
        status: 'ACTIVE',
        sortOrder: 0,
      },
      balanceRows: [],
      currencies: [],
      currenciesLoading: false,
      deleteDialog: false,
      deleting: false,
      accountToDelete: null,
      snapshots: [],
      snapshotsLoading: false,
      snapshotsRowsPerPage: 10,
      snapshotsPage: 1,
      snapshotDateFilter: '',
      snapshotDateMenu: false,
      expandedSnapshot: null,
      excelStartDate: '',
      excelEndDate: '',
      excelStartMenu: false,
      excelEndMenu: false,
    };
  },

  computed: {
    accountDialogHeadColor() {
      return Number(this.accountModel.accountType) === 1 ? 'amber darken-2' : 'primary';
    },
    accountDialogBtnColor() {
      return Number(this.accountModel.accountType) === 1 ? 'amber darken-2' : 'primary';
    },
    paginatedSnapshots() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.snapshotsPage || 1));
      const start = (page - 1) * per;
      return list.slice(start, start + per);
    },
    snapshotsTotalPages() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      return Math.max(1, Math.ceil(list.length / per));
    },
    snapshotsPaginationInfo() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.snapshotsPage || 1));
      const start = list.length === 0 ? 0 : (page - 1) * per + 1;
      const end = Math.min(page * per, list.length);
      const total = list.length;
      if (total === 0) return '0-0 de 0';
      return `${start}-${end} de ${total}`;
    },
  },

  methods: {
    onTabChanged() {
      if (this.activeTab === 1) this.loadSnapshots();
    },

    showError(error, fallback) {
      const msg = (error && error.response && error.response.data && error.response.data.message) || fallback;
      this.$store.state.message = msg;
      this.$store.state.dialog = true;
    },

    async loadCompanies() {
      try {
        const { data } = await companiesApi.getCompanies();
        this.companies = Array.isArray(data) ? data : [];
        if (this.selectedCompanyId === null && this.companies.length) {
          this.selectedCompanyId = this.companies[0].id;
        }
      } catch (e) {
        this.companies = [];
      }
    },

    async loadAccounts() {
      this.loading = true;
      try {
        const status = this.statusFilter ? this.statusFilter : undefined;
        this.accounts = (await this.$store.dispatch('cashAccounts/listCashAccounts', {
          companyId: this.selectedCompanyId,
          status,
        })) || [];
      } catch (e) {
        this.accounts = [];
        this.showError(e, 'Hesaplar yüklenemedi.');
      } finally {
        this.loading = false;
      }
    },

    async loadCurrencies() {
      this.currenciesLoading = true;
      try {
        this.currencies = (await this.$store.dispatch('cashAccounts/listCurrencies', true)) || [];
      } catch (e) {
        this.currencies = [];
      } finally {
        this.currenciesLoading = false;
      }
    },

    openAccountCreateDialog() {
      this.accountEditId = null;
      this.accountModel = {
        companyId: this.selectedCompanyId,
        name: '',
        accountType: 0,
        iban: '',
        status: 'ACTIVE',
        sortOrder: 0,
      };
      this.balanceRows = [];
      this.accountDialog = true;
    },

    async openAccountEditDialog(item) {
      this.accountEditId = item.id;
      this.accountModel = {
        companyId: item.companyId !== undefined && item.companyId !== null ? item.companyId : null,
        name: item.name || '',
        accountType: Number(item.accountType) || 0,
        iban: item.iban || '',
        status: item.status || 'ACTIVE',
        sortOrder: Number(item.sortOrder) || 0,
      };
      this.balanceRows = [];
      this.accountDialog = true;
      if (!this.currencies.length) {
        await this.loadCurrencies();
      }
      this.buildBalanceRows(item);
    },

    buildBalanceRows(item) {
      const existing = {};
      (item.balances || []).forEach((b) => {
        existing[b.currencyId] = Number(b.balance) || 0;
      });
      this.balanceRows = (this.currencies || []).map((c) => {
        const original = existing[c.id] !== undefined ? existing[c.id] : 0;
        return {
          currencyId: c.id,
          code: c.code || '',
          symbol: c.symbol || '',
          original,
          newBalance: original,
        };
      });
    },

    async saveAccount() {
      if (!this.accountModel.name || !this.accountModel.name.trim()) {
        this.$store.state.message = 'Hesap adı zorunludur.';
        this.$store.state.dialog = true;
        return;
      }
      if (this.accountModel.companyId === null || this.accountModel.companyId === undefined || this.accountModel.companyId === '') {
        this.$store.state.message = 'Şirket seçimi zorunludur.';
        this.$store.state.dialog = true;
        return;
      }
      this.accountSaving = true;
      try {
        const payload = {
          companyId: this.accountModel.companyId,
          name: this.accountModel.name.trim(),
          accountType: Number(this.accountModel.accountType) || 0,
          iban: (this.accountModel.iban || '').trim(),
          status: this.accountModel.status || 'ACTIVE',
          sortOrder: Number(this.accountModel.sortOrder) || 0,
        };
        if (this.accountEditId) {
          await this.$store.dispatch('cashAccounts/updateCashAccount', { id: this.accountEditId, payload });
          const changed = (this.balanceRows || []).filter((row) => Number(row.newBalance) !== Number(row.original));
          for (let i = 0; i < changed.length; i += 1) {
            const row = changed[i];
            await this.$store.dispatch('cashAccounts/cashAdjust', {
              accountId: this.accountEditId,
              currencyId: row.currencyId,
              newBalance: Number(row.newBalance) || 0,
              description: 'Düzenleme ekranından bakiye düzeltme',
            });
          }
        } else {
          await this.$store.dispatch('cashAccounts/createCashAccount', payload);
        }
        this.accountDialog = false;
        await this.loadAccounts();
      } catch (e) {
        this.showError(e, 'Hesap kaydedilemedi.');
      } finally {
        this.accountSaving = false;
      }
    },

    confirmDelete(item) {
      this.accountToDelete = item;
      this.deleteDialog = true;
    },

    async deleteAccount() {
      if (!this.accountToDelete) return;
      this.deleting = true;
      try {
        await this.$store.dispatch('cashAccounts/deleteCashAccount', this.accountToDelete.id);
        this.deleteDialog = false;
        this.accountToDelete = null;
        await this.loadAccounts();
      } catch (e) {
        this.showError(e, 'Hesap silinemedi.');
      } finally {
        this.deleting = false;
      }
    },

    async loadSnapshots() {
      this.snapshotsLoading = true;
      this.expandedSnapshot = null;
      try {
        const range = {
          startDate: this.snapshotDateFilter || null,
          endDate: this.snapshotDateFilter || null,
        };
        this.snapshots = (await this.$store.dispatch('cashAccounts/cashSnapshots', range)) || [];
        this.snapshotsPage = 1;
      } catch (e) {
        this.snapshots = [];
        this.showError(e, 'Snapshot kayıtları yüklenemedi.');
      } finally {
        this.snapshotsLoading = false;
      }
    },

    async exportSnapshotsExcel() {
      const XLSX = require('xlsx');
      let rows;
      try {
        rows = await this.$store.dispatch('cashAccounts/cashSnapshots', {
          startDate: this.excelStartDate,
          endDate: this.excelEndDate,
        });
      } catch (e) {
        this.showError(e, 'Snapshot kayıtları yüklenemedi.');
        return;
      }
      rows = rows || [];
      if (!rows.length) {
        this.$store.state.message = 'Seçilen aralıkta snapshot bulunamadı.';
        this.$store.state.dialog = true;
        return;
      }
      rows = rows.slice().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const wb = XLSX.utils.book_new();
      const usedNames = {};
      rows.forEach((item) => {
        const aoa = [];
        aoa.push(['Tarih', this.formatDateOnly(item.createdAt)]);
        aoa.push([]);
        aoa.push(['TOPLAMLAR']);
        aoa.push(['Para Birimi', 'Toplam']);
        this.getSnapshotTotals(item).forEach((t) => {
          aoa.push([
            this.snapVal(t, 'Code') || this.snapVal(t, 'Symbol'),
            Number(this.snapVal(t, 'Total')) || 0,
          ]);
        });
        aoa.push([]);
        aoa.push(['HESAPLAR']);
        aoa.push(['Hesap', 'Tip', 'Durum', 'Para Birimi', 'Bakiye']);
        this.getSnapshotAccounts(item).forEach((acc) => {
          const name = this.snapVal(acc, 'Name');
          const type = Number(this.snapVal(acc, 'AccountType')) === 1 ? 'Kasa' : 'Banka';
          const status = this.snapVal(acc, 'Status') === 'ACTIVE' ? 'Aktif' : 'Pasif';
          const bals = this.getSnapshotAccountBalances(acc);
          if (!bals.length) {
            aoa.push([name, type, status, '-', 0]);
          } else {
            bals.forEach((b) => {
              aoa.push([
                name,
                type,
                status,
                this.snapVal(b, 'Code') || this.snapVal(b, 'Symbol'),
                Number(this.snapVal(b, 'Balance')) || 0,
              ]);
            });
          }
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        let sheetName = String(this.formatDateOnly(item.createdAt)).replace(/[\\/?*[\]:]/g, '.').slice(0, 31);
        if (usedNames[sheetName]) {
          let counter = usedNames[sheetName] + 1;
          usedNames[sheetName] = counter;
          const suffix = ' (' + counter + ')';
          sheetName = (sheetName.slice(0, 31 - suffix.length) + suffix).slice(0, 31);
        } else {
          usedNames[sheetName] = 1;
        }
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      XLSX.writeFile(wb, 'ParaBanka_Snapshot_' + this.excelStartDate + '_' + this.excelEndDate + '.xlsx');
    },

    getSnapshotData(item) {
      if (!item || !item.message) return {};
      if (item._parsed) return item._parsed;
      try {
        const obj = JSON.parse(item.message);
        item._parsed = obj || {};
      } catch (e) {
        item._parsed = {};
      }
      return item._parsed;
    },

    snapVal(obj, key) {
      if (!obj) return undefined;
      const lower = key.charAt(0).toLowerCase() + key.slice(1);
      const val = obj[key] !== undefined && obj[key] !== null ? obj[key] : obj[lower];
      return val;
    },

    getSnapshotTotals(item) {
      const data = this.getSnapshotData(item);
      const list = (data && (data.TotalsByCurrency || data.totalsByCurrency)) || [];
      return Array.isArray(list) ? list : [];
    },

    getSnapshotAccounts(item) {
      const data = this.getSnapshotData(item);
      const list = (data && (data.Accounts || data.accounts)) || [];
      return Array.isArray(list) ? list : [];
    },

    getSnapshotAccountBalances(acc) {
      const list = this.snapVal(acc, 'Balances') || [];
      return Array.isArray(list) ? list : [];
    },

    toggleSnapshotDetail(i) {
      this.expandedSnapshot = this.expandedSnapshot === i ? null : i;
    },

    formatMoney(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '0,00';
      return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDate(date) {
      return date ? moment(date).format('DD.MM.YYYY HH:mm') : '-';
    },

    formatDateOnly(date) {
      return date ? moment(date).format('DD.MM.YYYY') : '-';
    },
  },

  async mounted() {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    this.excelStartDate = moment(first).format('YYYY-MM-DD');
    this.excelEndDate = moment(now).format('YYYY-MM-DD');
    await this.loadCompanies();
    this.loadAccounts();
  },
};
</script>

<style scoped>
.cash-tabs >>> .v-tabs__bar {
  background: linear-gradient(135deg, #1d4ed8, #2563eb) !important;
}

.cash-tabs >>> .v-tabs__item {
  color: #ffffff !important;
  opacity: 0.78;
  font-weight: 800;
  letter-spacing: 0.2px;
  text-transform: none;
}

.cash-tabs >>> .v-tabs__item--active {
  opacity: 1;
  font-weight: 900;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 10px;
}

.cash-tabs >>> .v-tabs__slider {
  background-color: #ffffff !important;
  height: 4px !important;
}

.v-chip {
  font-weight: 600;
}

.cash-modal {
  overflow: hidden;
}

.cash-balance-section {
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.cash-balance-section-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.75rem;
}

.cash-balance-note {
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.85rem;
}

.cash-tab-section {
  max-width: 80rem;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.refined-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.refined-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.025em;
}

.refined-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
}

.refined-primary-btn {
  display: inline-flex;
  align-items: center;
  background: #2563eb;
  color: #fff;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
  transition: background 0.2s, box-shadow 0.2s;
}

.refined-primary-btn:hover {
  background: #1d4ed8;
}

.refined-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refined-btn-success {
  background: #059669;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
}

.refined-btn-success:hover:not(:disabled) {
  background: #047857;
}

.refined-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.refined-date-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.refined-date-btn:hover {
  border-color: #2563eb;
  background: #f8fafc;
}

.refined-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.cash-excel-filters {
  align-items: flex-end;
}

.cash-excel-btn-item {
  justify-content: flex-end;
}

.refined-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.refined-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.refined-select {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #0f172a;
  min-width: 180px;
}

.refined-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.refined-table-wrap {
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.refined-table-scroll {
  overflow-x: auto;
}

.refined-table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
}

.refined-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.refined-table th {
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cash-th-actions {
  width: 120px;
  text-align: center;
}

.refined-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.refined-table tbody tr:hover {
  background: rgba(248, 250, 252, 0.7);
}

.refined-table td {
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: #475569;
}

.refined-cell-bold {
  font-weight: 600;
  color: #1e293b;
}

.refined-cell-value {
  font-weight: 500;
  color: #1e293b;
}

.refined-muted {
  color: #94a3b8;
}

.refined-chip {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.refined-chip-success {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.refined-chip-error {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.cash-chip-bank {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.cash-chip-vault {
  background: rgba(245, 158, 11, 0.2);
  color: #b45309;
}

.cash-chip-company {
  background: rgba(100, 116, 139, 0.14);
  color: #475569;
}

.cash-balances {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.cash-balance-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: #f1f5f9;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
}

.cash-balance-code {
  color: #64748b;
  font-weight: 700;
}

.cash-balance-amount {
  font-variant-numeric: tabular-nums;
}

.cash-balance-neg {
  background: rgba(239, 68, 68, 0.12);
}

.cash-balance-neg .cash-balance-amount,
.cash-balance-neg .cash-balance-code {
  color: #dc2626;
}

.cash-cell-actions {
  text-align: center;
  width: 120px;
}

.cash-detail-row td {
  background: #f8fafc;
  padding: 1rem 1.5rem;
}

.cash-detail-wrap {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .cash-detail-wrap {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .cash-detail-wrap {
    grid-template-columns: repeat(3, 1fr);
  }
}

.cash-detail-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.cash-detail-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
}

.cash-detail-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.9rem;
  margin-right: 0.25rem;
}

.refined-action-btn {
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.refined-action-btn:hover {
  background: #1d4ed8;
}

.refined-menu-card {
  border-radius: 0.625rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.refined-menu-body {
  padding: 0.5rem;
  min-width: 180px;
}

.refined-menu-btn {
  display: block;
  width: 100%;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.2s, filter 0.2s;
  margin-bottom: 0.375rem;
}

.refined-menu-btn:last-child {
  margin-bottom: 0;
}

.refined-menu-btn:hover {
  opacity: 0.92;
  filter: brightness(1.05);
}

.refined-menu-btn-primary {
  background: #2563eb;
}

.refined-menu-btn-amber {
  background: #d97706;
}

.refined-menu-btn-error {
  background: #dc2626;
}

.refined-menu-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 0.5rem 0;
}

.refined-table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.6);
  font-size: 0.875rem;
  color: #64748b;
  flex-wrap: wrap;
}

.refined-footer-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.refined-rows-select {
  background: transparent;
  border: none;
  color: #0f172a;
  cursor: pointer;
  font-weight: 500;
  padding: 0.25rem 0;
}

.refined-rows-select:focus {
  outline: none;
}

.refined-pagination-info {
  margin-left: auto;
}

.refined-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.refined-page-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.refined-page-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.refined-page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.refined-loading,
.refined-empty {
  text-align: center;
  padding: 2rem !important;
  color: #64748b;
}
</style>
